import type { ReactNode } from 'react';

/**
 * The chrome this app is built from, in one place.
 *
 * Written down because the first pass of the stock control tabs grew five
 * slightly different buttons and three different inputs. They follow the
 * catalogue and entity list, which are the closest thing this app has to a
 * house style. They started out beside stock control and moved here once a
 * second screen wanted them; a screen that needs something new should add it
 * here rather than keep its own.
 *
 * A handful of pieces are components rather than class strings, because they
 * carry markup as well as styling. The kanban and enquiry boards read them
 * from `shared/components/kanban` under their older `Board*` names.
 */

/**
 * `touch:py-3` on both, and on the toggle below: 12px above and below a 20px
 * line is a 44px target, which is the smallest a thumb hits reliably. The
 * variant is `(pointer: coarse)` — see the preset — so the mouse rendering is
 * the 32px it has always been, and a tablet gets the larger target at any
 * width.
 */
/* Flex, because preflight makes an `svg` `display: block` and a leading icon
   would otherwise drop onto its own line. `justify-center` keeps the label
   centred in a button a flex parent stretches, which `text-align` used to do;
   it beats a caller's justify-start/end, so override with justify-between. */
export const BUTTON_PRIMARY =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded bg-hilevel-red px-3 py-1.5 text-sm ' +
  'font-medium text-white touch:py-3 transition-opacity hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-40';

/* Filled, not white: an outlined white box is what `INPUT` below is, and the
   two were indistinguishable side by side in a toolbar. The fill is the cue —
   a raised face is pressed, a white field is typed into. */
export const BUTTON_SECONDARY =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded border border-hilevel-border ' +
  'bg-hilevel-control px-3 py-1.5 text-sm text-hilevel-slate touch:py-3 transition-colors ' +
  'hover:bg-hilevel-border disabled:cursor-not-allowed ' +
  'disabled:text-hilevel-gray disabled:hover:bg-hilevel-control';

/** A button that reads as a link: row actions inside a table. */
export const BUTTON_INLINE =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 bg-transparent text-sm text-hilevel-red ' +
  'hover:underline';

export const INPUT =
  'rounded border border-hilevel-border px-2 py-1 text-sm text-hilevel-slate ' +
  'placeholder:text-hilevel-gray focus:border-hilevel-red focus:outline-none';

/** An INPUT that opens a list. Same box; the pointer says it is not typed into. */
export const SELECT = `${INPUT} cursor-pointer`;

/**
 * An INPUT filling its column. What a form field is, as against the inputs
 * that sit in a toolbar and are only as wide as what goes in them.
 */
export const FIELD_CLASS = `${INPUT} w-full`;

export const LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-hilevel-gray';

export const ALERT =
  'rounded border border-hilevel-red bg-hilevel-red/10 px-3 py-2 text-sm text-hilevel-slate';

/** The strip of controls above a tab's content. */
export const TOOLBAR = 'flex flex-wrap items-center gap-2 border-b border-hilevel-border pb-3';

/**
 * The next three carry markup as well as classes. They started on the kanban
 * board, which still draws them as `BoardHeader`, `BoardFilterBar` and
 * `BoardToggle` — the boards follow whatever they say here.
 */

/** Page title plus right-aligned actions, above the content. */
export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    // `flex-wrap` with a row gap and no column gap: the actions drop under the
    // title on a narrow screen instead of crushing it, and a row that fits —
    // every desktop one — lays out exactly as it did, because a gap only
    // separates lines once there are two.
    <div className="flex flex-wrap items-center justify-between gap-y-2 px-4 py-4">
      <h2 className="text-lg font-semibold text-hilevel-slate">{title}</h2>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/** The filter strip between a page header and its content. */
export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 pb-2">
      <span className={LABEL}>Filter</span>
      {children}
    </div>
  );
}

/**
 * A two-or-more-way segmented toggle, replacing the Bootstrap btn-group. The
 * chosen segment is filled rather than outlined: at this size an outline is
 * not enough to say which one is on.
 */
export function SegmentedToggle<V extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: V;
  options: { value: V; label: string }[];
  onChange: (value: V) => void;
  ariaLabel?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex overflow-hidden rounded border border-hilevel-border"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          className={`cursor-pointer px-3 py-1.5 text-sm transition-colors touch:py-3 ${
            option.value === value
              ? 'bg-hilevel-red text-white'
              : 'bg-hilevel-control text-hilevel-slate hover:bg-hilevel-border'
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
